/**
 * URL Slug Utilities
 *
 * Provides deterministic slug generation and parsing for SEO-friendly URLs.
 * All URLs use consistent slugs across routing, linking, and metadata.
 *
 * Canonical URL architecture:
 *   /[state]/[suburb-slug]/gyms                → gym location directory
 *   /[state]/[suburb-slug]/gyms/[style-slug]   → style-filtered gym directory
 *   /[state]/[suburb-slug]/gyms/[gym-slug]-g[id] → gym profile
 *
 * The slug algorithm, state primitives, and URL builders live in the shared
 * state core (lib/stateCore.mjs) — the single source of truth also consumed
 * by scripts/slug-utils.mjs and vite.config.ts. This module re-exports them
 * (unchanged signatures; state params optional, defaulting to 'nsw') and adds
 * the app-side parse/display helpers.
 */

import { STYLE_OPTIONS } from './filters';
import { toStyleSlug } from './stateCore.mjs';

// ── Shared core re-exports (algorithm + parameterised URL builders) ─────────
export {
    normaliseSlug,
    canonicaliseSuburbName,
    toLocationSlug,
    toAddressedLocationSlug,
    getSuburbPostcode,
    toStyleSlug,
    toGymNameSlug,
    toGymSlug,
    buildDirectoryUrl,
    buildStyleUrl,
    buildGymUrl,
} from './stateCore.mjs';
export type { StateSlug } from './stateCore.mjs';

// ============================================
// DISPLAY HELPERS
// ============================================

/**
 * Convert a location slug back to a display-friendly suburb NAME.
 * Example: "surry-hills" → "Surry Hills", "hunters-hill-2110" → "Hunters Hill"
 *
 * Used in <title>, <h1>, meta description, breadcrumb labels and JSON-LD name fields.
 *
 * THE TRAILING POSTCODE IS DROPPED, and that single line is what keeps audit O6 from
 * breaking four things at once. Since the slug carries the suburb's postcode, a naive
 * title-case would return "Hunters Hill 2110", and every consumer would then publish
 * it: the title builder appends the state and postcode itself, so it would read
 * "Martial Arts Gyms in Hunters Hill 2110 NSW 2110"; the <h1>, the breadcrumb label and
 * the JSON-LD addressLocality would all name a suburb called "Hunters Hill 2110".
 *
 * It is also the SEARCH boundary. `useDirectoryFilters` derives `filters.suburb` from
 * this function, and functions/src/index.ts matches that string EXACTLY against the
 * stored locality — so a postcode surviving here returns ZERO gyms on every suburb
 * page, and does it invisibly, because the prerendered gyms render before any search
 * runs (the documented Saint→St trap). Dropping it here fixes the search at the same
 * seam as the copy, rather than in a second place that can drift from this one.
 *
 * Safe to strip unconditionally: no NSW locality slug contains a 4-digit token
 * (measured across all 601 built suburb slugs), so the only such token is the address
 * suffix this module appends. Round-trip is preserved through the address map, not
 * through the display string: "hunters-hill-2110" → "Hunters Hill" →
 * toAddressedLocationSlug → "hunters-hill-2110".
 */
export function toDisplaySuburb(slug: string): string {
    const cleanSlug = slug.startsWith('place-') ? slug.substring(6) : slug;
    const tokens = cleanSlug.split('-');
    if (tokens.length > 1 && /^\d{4}$/.test(tokens[tokens.length - 1]!)) tokens.pop();
    return tokens
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

/** An embedded Australian state/territory token sitting between a suburb and a trailing
 *  4-digit postcode, e.g. the " NSW" in "Alexandria NSW 2015". */
const EMBEDDED_STATE_TOKEN = /\s+(?:NSW|VIC|QLD|SA|WA|TAS|NT|ACT)(?=\s+\d{4}\s*$)/i;

/**
 * Normalise a location display string to the canonical "Suburb Postcode" form — matching
 * the backend's resolved format (`${locality} ${postcode}`, e.g. "Alexandria 2015") by
 * dropping an embedded state token. resolveNearestLocation and autocomplete suggestions
 * carry the fuller "Suburb STATE Postcode" shape, which would otherwise fail the directory
 * pill's postcode-match guard (that guard compares against the state/postcode-less suburb).
 */
export function normalizeLocationDisplay(display: string): string {
    return display.replace(EMBEDDED_STATE_TOKEN, '');
}

// ============================================
// SLUG PARSING
// ============================================

/**
 * Parse a style slug back to the canonical style name.
 * Returns null if the slug doesn't match any known style.
 * Example: "muay-thai" → "Muay Thai"
 */
export function parseStyleSlug(slug: string): string | null {
    const normalizedSlug = slug.toLowerCase();

    // Find matching canonical style
    const match = STYLE_OPTIONS.find(style =>
        toStyleSlug(style) === normalizedSlug
    );

    return match || null;
}

/** Result of parsing a gym URL slug — the ID may be a stableId or a legacy Place ID */
export interface ParsedGymId {
    id: string;
    type: 'stable' | 'placeId';
}

/**
 * Typed version of parseGymId that also indicates whether the ID is
 * a stable internal ID or a legacy Google Place ID.
 */
export function parseGymIdTyped(slugWithId: string): ParsedGymId | null {
    // 1. Try canonical -g prefix (e.g., /name-gxK9m2pLq or /name-gChIJ...)
    const gMatch = slugWithId.match(/-g([^-]+)$/);
    if (gMatch) {
        const id = gMatch[1]!; // safe: regex group 1 ([^-]+) is guaranteed on a successful match
        // Place IDs start with ChIJ and are 20+ chars
        const isPlaceId = id.startsWith('ChIJ') && id.length > 20;
        return { id, type: isPlaceId ? 'placeId' : 'stable' };
    }

    // 2. Fallback: try direct ID suffix (e.g., /name-ChIJ...)
    const parts = slugWithId.split('-');
    const lastPart = parts[parts.length - 1]!; // safe: String.split always returns ≥1 element

    if (lastPart.startsWith('ChIJ') && lastPart.length > 20) {
        return { id: lastPart, type: 'placeId' };
    }

    return null;
}
